import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ComposerDocument, DocumentSummary } from './types';
import {
  deleteDocument, duplicateDocument, getActiveId, listDocuments,
  loadDocument, makeNewDocument, saveDocument, setActiveId,
} from './state/store';
import {
  appendChild, duplicateBlock, insertAfter, makeBlock, moveBlock, removeBlock, updateBlockProps,
} from './state/tree';
import { documentToStandaloneHtml, treeToHtml } from './state/serializer';
import { Sidebar } from './ui/Sidebar';
import { Toolbar } from './ui/Toolbar';
import { Canvas } from './ui/Canvas';
import { Inspector } from './inspectors/Inspector';
import { newDocumentFromTemplate, type TemplateDef } from './templates';
import { THEMES, type ThemeEntry } from './themes';
import './composer.css';

export interface DocumentComposerProps {
  /** Optional theme registry override (defaults to the full bundled set of 60+). */
  themes?: ThemeEntry[];
}

export function DocumentComposer({ themes = THEMES }: DocumentComposerProps) {
  const [documents, setDocuments] = useState<DocumentSummary[]>(() => listDocuments());
  const [activeId, setActiveIdState] = useState<string | null>(() => getActiveId());
  const [doc, setDoc] = useState<ComposerDocument | null>(() => {
    const id = getActiveId();
    return id ? loadDocument(id) : null;
  });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [mode, setMode] = useState<'edit' | 'preview'>('edit');
  const [saving, setSaving] = useState(false);

  /* Refresh sidebar list whenever the doc changes. */
  const refreshList = useCallback(() => setDocuments(listDocuments()), []);

  /* Auto-save on every doc mutation. Debounce ~300ms so a hot keypress
     doesn't write to localStorage on every char. */
  const saveTimer = useRef<number | null>(null);
  useEffect(() => {
    if (!doc) return;
    setSaving(true);
    if (saveTimer.current) window.clearTimeout(saveTimer.current);
    saveTimer.current = window.setTimeout(() => {
      const cached: ComposerDocument = { ...doc, htmlCache: treeToHtml(doc.tree) };
      saveDocument(cached);
      refreshList();
      setSaving(false);
    }, 300);
    return () => {
      if (saveTimer.current) window.clearTimeout(saveTimer.current);
    };
  }, [doc, refreshList]);

  /* Switch active document. */
  const openDocument = useCallback((id: string) => {
    const next = loadDocument(id);
    if (!next) return;
    setActiveId(id);
    setActiveIdState(id);
    setDoc(next);
    setSelectedId(null);
  }, []);

  const handleNewFromTemplate = useCallback((t: TemplateDef) => {
    const created = newDocumentFromTemplate(t);
    saveDocument(created);
    setActiveId(created.id);
    setActiveIdState(created.id);
    setDoc(created);
    setSelectedId(null);
    refreshList();
  }, [refreshList]);

  const handleNewBlank = useCallback(() => {
    const created = makeNewDocument({ name: 'Untitled document', tree: [] });
    saveDocument(created);
    setActiveId(created.id);
    setActiveIdState(created.id);
    setDoc(created);
    refreshList();
  }, [refreshList]);

  /* If there's no doc on first load, drop into a fresh blank one so the
     user always has a canvas. */
  useEffect(() => {
    if (!doc && documents.length === 0) handleNewBlank();
  }, [doc, documents.length, handleNewBlank]);

  /* Sidebar handlers. */
  const handleDuplicate = (id: string) => {
    const cloned = duplicateDocument(id);
    if (cloned) {
      refreshList();
      openDocument(cloned.id);
    }
  };
  const handleDelete = (id: string) => {
    deleteDocument(id);
    if (activeId === id) {
      setDoc(null);
      setActiveIdState(null);
    }
    refreshList();
  };
  const handleRename = (id: string, name: string) => {
    const target = loadDocument(id);
    if (!target) return;
    const updated = saveDocument({ ...target, name });
    if (id === activeId) setDoc(updated);
    refreshList();
  };

  /* Mutation helpers. All mutations go through here so saving stays atomic. */
  const mutate = useCallback((fn: (d: ComposerDocument) => ComposerDocument) => {
    setDoc((prev) => (prev ? fn(prev) : prev));
  }, []);

  const onPatchDocument = useCallback((patch: Partial<ComposerDocument>) => {
    mutate((d) => ({ ...d, ...patch }));
  }, [mutate]);

  const onUpdateBlockProps = useCallback((id: string, patch: Record<string, unknown>) => {
    mutate((d) => ({ ...d, tree: updateBlockProps(d.tree, id, patch) }));
  }, [mutate]);

  const onAddRootBlock = useCallback((type: string) => {
    const created = makeBlock(type);
    mutate((d) => ({ ...d, tree: insertAfter(d.tree, selectedId, created) }));
    setSelectedId(created.id);
  }, [mutate, selectedId]);

  const onAddChildBlock = useCallback((parentId: string, type: string) => {
    if (!type) return;
    const created = makeBlock(type);
    mutate((d) => ({ ...d, tree: appendChild(d.tree, parentId, created) }));
    setSelectedId(created.id);
  }, [mutate]);

  const onDeleteBlock = useCallback((id: string) => {
    mutate((d) => ({ ...d, tree: removeBlock(d.tree, id) }));
    setSelectedId(null);
  }, [mutate]);

  const onDuplicateBlock = useCallback((id: string) => {
    mutate((d) => ({ ...d, tree: duplicateBlock(d.tree, id) }));
  }, [mutate]);

  const onMoveBlock = useCallback((id: string, dir: -1 | 1) => {
    mutate((d) => ({ ...d, tree: moveBlock(d.tree, id, dir) }));
  }, [mutate]);

  /* Print uses the browser's native dialog. We hide everything except the
     `.sheet` element via print.css. */
  const handlePrint = useCallback(() => {
    setMode('preview');
    setSelectedId(null);
    /* Wait one tick so the DOM updates before the print dialog opens. */
    requestAnimationFrame(() => requestAnimationFrame(() => window.print()));
  }, []);

  const handleExport = useCallback(() => {
    if (!doc) return;
    const html = documentToStandaloneHtml(doc);
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${doc.name.replace(/[^a-z0-9-_]+/gi, '_') || 'document'}.html`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }, [doc]);

  const themeOptions = useMemo(() => themes, [themes]);

  /* If we deleted the active doc, drop a placeholder. */
  if (!doc) {
    return (
      <div className="cb-app">
        <Sidebar
          documents={documents}
          activeId={activeId}
          onPick={openDocument}
          onNewFromTemplate={handleNewFromTemplate}
          onDuplicate={handleDuplicate}
          onDelete={handleDelete}
          onRename={handleRename}
        />
        <main className="cb-main cb-main-empty">
          <p>No document selected. Pick one on the left or create a new one.</p>
        </main>
      </div>
    );
  }

  return (
    <div className={`cb-app mode-${mode}`}>
      <Sidebar
        documents={documents}
        activeId={activeId}
        onPick={openDocument}
        onNewFromTemplate={handleNewFromTemplate}
        onDuplicate={handleDuplicate}
        onDelete={handleDelete}
        onRename={handleRename}
      />
      <main className="cb-main">
        <Toolbar
          mode={mode}
          onModeChange={setMode}
          onPrint={handlePrint}
          onExportHtml={handleExport}
          docName={doc.name}
          onDocNameChange={(name) => onPatchDocument({ name })}
          saving={saving}
        />
        <Canvas
          doc={doc}
          mode={mode}
          selectedId={selectedId}
          onSelect={setSelectedId}
          onUpdateBlockProps={onUpdateBlockProps}
        />
      </main>
      {mode === 'edit' && (
        <Inspector
          doc={doc}
          selectedId={selectedId}
          onPatchDocument={onPatchDocument}
          onUpdateBlockProps={onUpdateBlockProps}
          onAddRootBlock={onAddRootBlock}
          onAddChildBlock={onAddChildBlock}
          onDeleteBlock={onDeleteBlock}
          onDuplicateBlock={onDuplicateBlock}
          onMoveBlock={onMoveBlock}
          themes={themeOptions}
        />
      )}
    </div>
  );
}

export default DocumentComposer;
